import { useState, useEffect } from 'react';
import { Button, Modal, Input, Select } from '@/components/common';
import { platformMdas, type MDA } from '@/services/platform';
import { PlatformBulkImportModal } from '@/components/staff/PlatformBulkImportModal';
import styles from './MDAsPage.module.css';

export function MDAsPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingMda, setEditingMda] = useState<MDA | null>(null);
  const [mdas, setMdas] = useState<MDA[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    shortName: '',
    type: 'ministry' as 'ministry' | 'department' | 'agency',
    contactEmail: '',
    address: '',
  });

  useEffect(() => {
    fetchMdas();
  }, []);

  async function fetchMdas() {
    setLoading(true);
    setError(null);
    try {
      const response = await platformMdas.list();
      if (response.success && response.data) {
        setMdas(response.data);
      } else {
        const errorMsg = response.error?.message || 'Unknown error';
        setError(`Failed to load institutions: ${errorMsg}`);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Network error';
      setError(`Failed to load institutions: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setFormData({
      name: '',
      code: '',
      shortName: '',
      type: 'ministry',
      contactEmail: '',
      address: '',
    });
  }

  function openCreateModal() {
    resetForm();
    setEditingMda(null);
    setIsCreateModalOpen(true);
  }

  function openEditModal(mda: MDA) {
    setFormData({
      name: mda.name,
      code: mda.code,
      shortName: mda.shortName,
      type: mda.type,
      contactEmail: mda.contactEmail || '',
      address: mda.address || '',
    });
    setEditingMda(mda);
    setIsCreateModalOpen(true);
  }

  function closeModal() {
    setIsCreateModalOpen(false);
    setEditingMda(null);
    resetForm();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (editingMda) {
        // Update existing MDA
        const response = await platformMdas.update(editingMda.id, {
          name: formData.name,
          shortName: formData.shortName,
          type: formData.type,
          contactEmail: formData.contactEmail || null,
          address: formData.address || null,
        });
        if (response.success) {
          await fetchMdas();
          closeModal();
        } else {
          setError('Failed to update institution');
        }
      } else {
        // Create new MDA
        const response = await platformMdas.create({
          code: formData.code.toUpperCase(),
          name: formData.name,
          shortName: formData.shortName,
          type: formData.type,
          contactEmail: formData.contactEmail || undefined,
          address: formData.address || undefined,
        });
        if (response.success) {
          await fetchMdas();
          closeModal();
        } else {
          setError('Failed to create institution');
        }
      }
    } catch {
      setError(editingMda ? 'Failed to update institution' : 'Failed to create institution');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleStatus(mda: MDA) {
    try {
      const response = await platformMdas.update(mda.id, {
        isActive: !mda.isActive,
      });
      if (response.success) {
        await fetchMdas();
      }
    } catch {
      setError('Failed to update institution status');
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>MDAs Management</h1>
          <p className={styles.subtitle}>Manage institutions and their configurations</p>
        </div>
        <div className={styles.headerActions}>
          <Button variant="secondary" onClick={() => setShowBulkImport(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            Bulk Import Staff
          </Button>
          <Button onClick={openCreateModal}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Add MDA
          </Button>
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button onClick={() => setError(null)} className={styles.errorClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>Loading institutions...</div>
      ) : mdas.length === 0 ? (
        <div className={styles.emptyState}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <path d="M19 21V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V21M19 21H5M19 21H21M5 21H3M9 7H10M9 11H10M14 7H15M14 11H15M12 21V16H9V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <p>No institutions onboarded yet</p>
          <Button onClick={openCreateModal} variant="secondary">Add First Institution</Button>
        </div>
      ) : (
        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <div className={styles.tableCell}>Institution</div>
            <div className={styles.tableCell}>Code</div>
            <div className={styles.tableCell}>Type</div>
            <div className={styles.tableCell}>Status</div>
            <div className={styles.tableCell}>Actions</div>
          </div>
          <div className={styles.tableBody}>
            {mdas.map((mda) => (
              <div key={mda.id} className={styles.tableRow}>
                <div className={styles.tableCell}>
                  <div className={styles.mdaInfo}>
                    <span className={styles.mdaName}>{mda.name}</span>
                    {mda.contactEmail && (
                      <span className={styles.mdaEmail}>{mda.contactEmail}</span>
                    )}
                  </div>
                </div>
                <div className={styles.tableCell}>
                  <span className={styles.mdaCode}>{mda.code}</span>
                </div>
                <div className={styles.tableCell}>
                  <span className={styles.mdaType}>{mda.type}</span>
                </div>
                <div className={styles.tableCell}>
                  <span className={`${styles.status} ${mda.isActive ? styles.active : styles.inactive}`}>
                    {mda.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className={styles.tableCell}>
                  <div className={styles.actions}>
                    <button
                      className={styles.actionButton}
                      title="Edit"
                      onClick={() => openEditModal(mda)}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.388 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      className={styles.actionButton}
                      title={mda.isActive ? 'Deactivate' : 'Activate'}
                      onClick={() => handleToggleStatus(mda)}
                    >
                      {mda.isActive ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M10 6L6 10M6 6L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M6 8L7.5 9.5L10 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeModal}
        title={editingMda ? 'Edit Institution' : 'Add New Institution'}
      >
        <form className={styles.createForm} onSubmit={handleSubmit}>
          <Input
            label="Institution Name"
            placeholder="e.g., Ministry of Finance"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Short Name"
            placeholder="e.g., Finance Ministry"
            value={formData.shortName}
            onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
            required
          />
          <Input
            label="Institution Code"
            placeholder="e.g., MOF"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            required
            disabled={!!editingMda}
          />
          <Select
            label="Institution Type"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as 'ministry' | 'department' | 'agency' })}
            required
          >
            <option value="ministry">Ministry</option>
            <option value="department">Department</option>
            <option value="agency">Agency</option>
          </Select>
          <Input
            label="Contact Email"
            type="email"
            placeholder="admin@mof.gov.gh"
            value={formData.contactEmail}
            onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
          />
          <Input
            label="Address"
            placeholder="P.O. Box ..."
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          />
          <div className={styles.modalActions}>
            <Button variant="secondary" type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : editingMda ? 'Update Institution' : 'Create Institution'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Bulk Import Modal */}
      <PlatformBulkImportModal
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onSuccess={() => {
          fetchMdas();
        }}
      />
    </div>
  );
}
